import React, { useState } from 'react';
import { Button } from '../ui/button';
import FundingLenderCriteriaPanel from './FundingLenderCriteriaPanel';
import FundingLenderForm from './FundingLenderForm';
import FundingLenderPerformancePanel from './FundingLenderPerformancePanel';
import FundingLenderProductForm from './FundingLenderProductForm';
import FundingLenderProductTable from './FundingLenderProductTable';
import FundingLenderStatusBadge from './FundingLenderStatusBadge';
import FundingPanel from './FundingPanel';

const FundingLenderDetail = ({
  detail,
  loading,
  saving,
  canEdit,
  onUpdate,
  onArchive,
  onAddProduct,
  onDeleteProduct,
}) => {
  const [editing, setEditing] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState('');

  if (loading) return <p className="text-sm text-[var(--fg-muted)]">Loading lender...</p>;
  if (!detail?.lender) return <p className="text-sm text-[var(--fg-muted)]">Lender not found.</p>;

  const { lender, performance, recentMatches, recentSubmissions, recentDeals } = detail;

  const handleArchive = async () => {
    const confirmed = window.confirm(`Archive ${lender.name}? Historical performance will remain in reports.`);
    if (!confirmed) return;
    await onArchive?.();
  };

  const handleDeleteProduct = async (product) => {
    if (!product.id) return;
    const confirmed = window.confirm(`Remove product ${product.name || product.productType}?`);
    if (!confirmed) return;
    setDeletingProductId(product.id);
    try {
      await onDeleteProduct?.(product.id);
    } finally {
      setDeletingProductId('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-[var(--fg-text)]">{lender.name}</h2>
            <FundingLenderStatusBadge status={lender.status} />
            {lender.isFeatured ? <span className="text-xs font-semibold text-[var(--fg-accent)]">Featured</span> : null}
          </div>
          <div className="mt-1 text-sm text-[var(--fg-muted)]">
            {lender.type}
            {lender.website ? ` · ${lender.website}` : ''}
          </div>
          {lender.description ? <p className="mt-2 text-sm text-[var(--fg-muted)]">{lender.description}</p> : null}
        </div>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setEditing((v) => !v)}>
              {editing ? 'Close editor' : 'Edit lender'}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-xl text-rose-600" onClick={handleArchive} disabled={saving}>
              Archive
            </Button>
          </div>
        ) : null}
      </div>

      {editing && canEdit ? (
        <FundingPanel title="Edit lender" subtitle="Platform catalog criteria">
          <FundingLenderForm
            lender={lender}
            saving={saving}
            onCancel={() => setEditing(false)}
            onSubmit={async (payload) => {
              await onUpdate?.(payload);
              setEditing(false);
            }}
          />
        </FundingPanel>
      ) : null}

      <FundingPanel title="Criteria" subtitle="Preferred fit requirements">
        <FundingLenderCriteriaPanel lender={lender} />
      </FundingPanel>

      <FundingPanel
        title="Products"
        subtitle="Lender product catalog"
        actions={canEdit ? (
          <Button size="sm" className="rounded-xl" onClick={() => setShowProductForm((v) => !v)}>
            {showProductForm ? 'Cancel' : 'Add product'}
          </Button>
        ) : null}
      >
        {showProductForm && canEdit ? (
          <div className="mb-4">
            <FundingLenderProductForm
              saving={saving}
              onCancel={() => setShowProductForm(false)}
              onSubmit={async (payload) => {
                await onAddProduct?.(payload);
                setShowProductForm(false);
              }}
            />
          </div>
        ) : null}
        <FundingLenderProductTable
          products={lender.products}
          canEdit={canEdit}
          deletingId={deletingProductId}
          onDelete={handleDeleteProduct}
        />
      </FundingPanel>

      <FundingPanel title="Historical performance" subtitle="Workspace pipeline metrics">
        <FundingLenderPerformancePanel performance={performance} />
      </FundingPanel>

      <div className="grid gap-4 xl:grid-cols-3">
        <FundingPanel title="Recent matches" subtitle="Latest preferred fit results">
          {(recentMatches || []).length ? (
            <ul className="space-y-2 text-sm">
              {recentMatches.map((match) => (
                <li key={match.id} className="rounded-xl border border-[var(--fg-line)] px-3 py-2">
                  <div className="font-medium">{match.clientName}</div>
                  <div className="text-[var(--fg-muted)]">{match.productType} · {match.matchScore}% · {match.fitTier}</div>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[var(--fg-muted)]">No recent matches.</p>}
        </FundingPanel>

        <FundingPanel title="Recent submissions" subtitle="Application pipeline">
          {(recentSubmissions || []).length ? (
            <ul className="space-y-2 text-sm">
              {recentSubmissions.map((submission) => (
                <li key={submission.id} className="rounded-xl border border-[var(--fg-line)] px-3 py-2">
                  <div className="font-medium">{submission.clientName}</div>
                  <div className="text-[var(--fg-muted)]">{submission.productType} · {submission.status}</div>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[var(--fg-muted)]">No recent submissions.</p>}
        </FundingPanel>

        <FundingPanel title="Recent deals" subtitle="Pipeline outcomes">
          {(recentDeals || []).length ? (
            <ul className="space-y-2 text-sm">
              {recentDeals.map((deal) => (
                <li key={deal.id} className="rounded-xl border border-[var(--fg-line)] px-3 py-2">
                  <div className="font-medium">{deal.clientName}</div>
                  <div className="text-[var(--fg-muted)]">{deal.stage} · {deal.productType}</div>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[var(--fg-muted)]">No recent deals.</p>}
        </FundingPanel>
      </div>
    </div>
  );
};

export default FundingLenderDetail;
